import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {DateFormat} from "../../../utility/DateFormat";
import {
   Consumer,
   ConsumerIdentifierEnum,
   FilterFindManyConsumerInput,
   SortFindManyConsumerInput,
   useBrandConsumersPageIntegrationMessageQuery,
   useBrandConsumersPageMergeMutation,
   useBrandConsumersPagePaginationLazyQuery,
   useBrandConsumersPageRequestPotentialsMutation,
   useBrandConsumersPageRequestPotentialsShopsQuery,
   useBrandConsumersPageSelectAllLazyQuery,
   useBrandConsumersPageShopsQuery,
} from "../../../Queries";
import {Merge} from "@mui/icons-material";
import {ParamBrandHouse} from "../../../Parameters";
import {RangeList} from "../../../components/rangelist/RangeList";
import {RangeCommand, RangeFilter, RangeListColumn, RangeSort, RangeTab} from "../../../components/rangelist/RangeListTypes";
import {ConsumerPagePath} from "../../shop/shopdata/consumers/ConsumerPage";
import {
   Alert,
   Box,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle
} from "@mui/material";
import Button from "@mui/material/Button";
import {FormProvider, useForm} from "react-hook-form";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {FormInputSelect, SelectOption} from "../../shop/wholesale/form/FormInputSelect";
import {ViewFilter} from "../../../components/ViewFilter";

const PAGE_SIZE = 30;

export const BrandConsumersPagePath = (brandHouseId: string) => {
   return "/brands/" + brandHouseId + "/consumers";
};

/** Opens the brand consumer list and restores any active consumer data view. */
export const BrandConsumersPage: FC<any> = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>();
   return <Page>
      <PageHeader title="Consumers"/>
      <PageContent fullHeight>
         <ViewFilter dataType="consumers">
            {(viewId) => <ConsumerList key={`${brandHouseId}:${viewId || ''}`} brandHouseId={brandHouseId} viewId={viewId}/>}
         </ViewFilter>
      </PageContent>
   </Page>;
};

type ConsumerListProps = {
   brandHouseId?: string;
   viewId?: string;
   readOnly?: boolean;
};

/** Lists consumers within the brand and view filters; management is limited to unfiltered brand lists. */
export const ConsumerList: FC<ConsumerListProps> = ({brandHouseId, viewId, readOnly = false}) => {
   const canManage = !!brandHouseId && !viewId && !readOnly;
   const navigate = useNavigate();
   const [requestPotentialsOpen, setRequestPotentialsOpen] = useState(false);

   const [fetchPagination] = useBrandConsumersPagePaginationLazyQuery();
   const [fetchAllIds] = useBrandConsumersPageSelectAllLazyQuery();
   const [consumerMerge] = useBrandConsumersPageMergeMutation();

   const {data: shopsData} = useBrandConsumersPageShopsQuery({
      variables: {brandHouseId: brandHouseId!},
      skip: !brandHouseId
   });

   const sortSpec: RangeSort<SortFindManyConsumerInput>[] = [{
      id: 'CreatedDesc',
      label: 'Created (newest first)',
      value: SortFindManyConsumerInput._ID_DESC
   }, {
      id: 'CreatedAsc',
      label: 'Created (oldest first)',
      value: SortFindManyConsumerInput._ID_ASC
   }, {
      id: 'MailAsc',
      label: 'Email (A-Z)',
      value: SortFindManyConsumerInput.SHOPID__MAIL_ASC
   }, {
      id: 'MailDesc',
      label: 'Email (Z-A)',
      value: SortFindManyConsumerInput.SHOPID__MAIL_DESC
   }];

   const filterSpec: RangeFilter<FilterFindManyConsumerInput>[] = useMemo(() => {
      const filters: RangeFilter<FilterFindManyConsumerInput>[] = [];

      // Shop filter (dynamic based on shops query)
      const shops = shopsData?.ShopMany || [];
      if (shops.length > 1) {
         filters.push({
            id: 'shopId',
            label: 'Shop',
            options: shops.map(shop => ({
               id: shop._id,
               label: shop.name || 'Unknown',
               filter: {
                  shopId: shop._id
               }
            }))
         });
      }

      // Accepts marketing filter
      filters.push({
         id: 'acceptsMarketing',
         label: 'Marketing',
         options: [{
            label: 'Accepts marketing',
            filter: {acceptsMarketing: true}
         }, {
            label: 'No marketing',
            filter: {acceptsMarketing: false}
         }]
      });

      // Self created filter
      filters.push({
         id: 'selfCreated',
         label: 'Source',
         options: [{
            label: 'Self-registered',
            filter: {selfCreated: true}
         }, {
            label: 'Created by system',
            filter: {selfCreated: false}
         }]
      });

      // Proxy account filter
      filters.push({
         id: 'proxyAccount',
         label: 'Account type',
         options: [{
            label: 'Proxy account',
            filter: {proxyAccount: true}
         }, {
            label: 'Regular account',
            filter: {proxyAccount: false}
         }]
      });

      // Disable contact filter
      filters.push({
         id: 'disableContact',
         label: 'Contact',
         options: [{
            label: 'Contact enabled',
            filter: {disableContact: false}
         }, {
            label: 'Contact disabled',
            filter: {disableContact: true}
         }]
      });

      return filters;
   }, [shopsData]);

   const tabsSpec: RangeTab[] = useMemo(() => {
      return [{
         id: 'all',
         label: 'All',
         filters: []
      }];
   }, []);

   const columns: RangeListColumn<Consumer>[] = useMemo(() => {
      return [{
         id: 'name',
         label: 'Name',
         get: (row) => row.name || 'No Name',
         width: 15,
         ellipsis: true
      }, {
         id: 'mail',
         label: 'Email',
         field: 'mail',
         width: 20,
         ellipsis: true
      }, {
         id: 'country',
         label: 'Country',
         get: (row) => row.country || row.countryCode || '-',
         width: 8
      }, {
         id: 'tradeIns',
         label: 'Trade-ins',
         get: (row) => row.statistics?.tradeIns ?? '-',
         width: 7
      }, {
         id: 'orders',
         label: 'Orders',
         get: (row) => row.statistics?.orders ?? '-',
         width: 7
      }, {
         id: 'phone',
         label: 'Phone',
         field: 'phone',
         width: 10,
         ellipsis: true
      }, {
         id: 'address',
         label: 'Address',
         get: (row) => {
            return [row.add1, row.add2, row.zipCode, row.city].filter(Boolean).join(' ');
         },
         width: 20,
         ellipsis: true
      }, {
         id: 'shop',
         label: 'Shop',
         get: (row) => row.shop?.name || '-',
         width: 12,
         ellipsis: true
      }, {
         id: 'marketing',
         label: 'Marketing',
         field: 'acceptsMarketing',
         render: (accepts) => accepts ? 'Yes' : 'No',
         width: 8
      }, {
         id: 'created',
         label: 'Created',
         get: (row) => row.createdAt ? new Date(row.createdAt).getTime() : 0,
         render: (dateTs) => dateTs ? DateFormat.toPresent(new Date(dateTs)) : '-',
         width: 10,
         ellipsis: true
      }, {
         id: 'lastLogin',
         label: 'Last login',
         get: (row) => row.lastLogin ? new Date(row.lastLogin).getTime() : 0,
         render: (dateTs) => dateTs ? DateFormat.toPresent(new Date(dateTs)) : '-',
         width: 10,
         ellipsis: true
      }];
   }, []);

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter: any) => {
      const effectiveFilter: FilterFindManyConsumerInput = {
         ...filter,
         brandHouseId: brandHouseId,
      };

      const {data} = await fetchPagination({
         variables: {
            page: page,
            perPage: perPage,
            search: search,
            sort: sort as SortFindManyConsumerInput || SortFindManyConsumerInput._ID_DESC,
            filter: effectiveFilter,
            viewId
         }
      });

      return {
         data: (data?.ConsumerPagination?.items || []) as Consumer[],
         totalCount: data?.ConsumerPagination?.count || 0
      };
   }, [fetchPagination, brandHouseId, viewId]);

   const onSelectAll = useCallback(async (filter?: FilterFindManyConsumerInput): Promise<string[]> => {
      const effectiveFilter: FilterFindManyConsumerInput = {
         ...filter,
         brandHouseId: brandHouseId,
      };

      const {data} = await fetchAllIds({
         variables: {
            filter: effectiveFilter,
            viewId
         }
      });

      return data?.ConsumerPagination?.items?.map(item => item._id) || [];
   }, [fetchAllIds, brandHouseId, viewId]);

   const onHandleClick = useCallback((row: Consumer, newWindow?: boolean) => {
      if (row.shopId) {
         const path = ConsumerPagePath(brandHouseId!, row.shopId, row._id);
         if (newWindow) {
            window.open(path, '_blank', '');
         } else {
            navigate(path);
         }
      }
   }, [navigate, brandHouseId]);

   const commands: RangeCommand<Consumer>[] = useMemo(() => {
      return [{
         id: 'merge',
         icon: Merge,
         title: 'Merge',
         execute: async (rowIds) => {
            await consumerMerge({
               variables: {
                  consumerIds: rowIds
               }
            });
         }
      }];
   }, [consumerMerge]);

   return <>
      {canManage && <Box sx={{mb: 2}}>
         <Button variant="outlined" onClick={() => setRequestPotentialsOpen(true)}>Import consumer data</Button>
      </Box>}
      {canManage && requestPotentialsOpen && (
         <RequestPotentialsDialog
            brandHouseId={brandHouseId!}
            onClose={() => setRequestPotentialsOpen(false)}
            onConsumerFound={(shopId, consumerId) => {
               setRequestPotentialsOpen(false);
               navigate(ConsumerPagePath(brandHouseId!, shopId, consumerId));
            }}
         />
      )}
      <RangeList
         globalId={brandHouseId ? "brandConsumers" : "allConsumers"}
         columns={columns}
         filters={filterSpec}
         sorts={sortSpec}
         tabs={tabsSpec}
         itemsPerPage={PAGE_SIZE}
         selections={canManage}
         commands={canManage ? commands : undefined}
         onSelectRow={canManage ? onHandleClick : undefined}
         onRequestData={onRequestData}
         onSelectAll={canManage ? onSelectAll : undefined}
         searchLabel="Search consumers..."
         options={{
            itemHeight: 55
         }}
      />
   </>;
};

type RequestPotentialsDialogProps = {
   brandHouseId: string;
   onClose: () => void;
   onConsumerFound: (shopId: string, consumerId: string) => void;
};

type RequestPotentialsFormData = {
   shopId: string;
   identifier: string;
   identifierType: ConsumerIdentifierEnum;
};

const identifierTypeOptions: SelectOption[] = [
   {id: ConsumerIdentifierEnum.EMAIL, name: 'Email'},
   {id: ConsumerIdentifierEnum.PHONE, name: 'Phone'},
   {id: ConsumerIdentifierEnum.EXTERNALID, name: 'External ID'},
];

const RequestPotentialsDialog: FC<RequestPotentialsDialogProps> = ({brandHouseId, onClose, onConsumerFound}) => {
   const [requestPotentials] = useBrandConsumersPageRequestPotentialsMutation();
   const [tokens, setTokens] = useState<string[]>([]);
   const [error, setError] = useState<string>();

   const {data: shopsData} = useBrandConsumersPageRequestPotentialsShopsQuery({
      variables: {brandHouseId},
   });
   const shops = shopsData?.ShopMany || [];

   const shopOptions: SelectOption[] = useMemo(() =>
      shops.map(shop => ({id: shop._id, name: shop.name || 'Unknown'})),
   [shops]);

   const form = useForm<RequestPotentialsFormData>({
      defaultValues: {
         shopId: '',
         identifier: '',
         identifierType: ConsumerIdentifierEnum.EMAIL,
      }
   });
   const {handleSubmit, getValues, setValue} = form;

   useEffect(() => {
      if (shops.length === 1) {
         setValue('shopId', shops[0]._id);
      }
   }, [shops, setValue]);

   const {data: messageData, stopPolling} = useBrandConsumersPageIntegrationMessageQuery({
      variables: {tokens},
      skip: tokens.length === 0,
      pollInterval: 2000,
   });

   const polling = tokens.length > 0;
   const messages = messageData?.IntegrationMessage || [];

   useEffect(() => {
      if (!polling || messages.length === 0) return;

      const failed = messages.some(msg => msg?.messageFailed);
      if (failed) {
         stopPolling();
         setError('Request failed');
         return;
      }

      const completedMsg = messages.find(msg => msg?.result?.consumerId);
      if (completedMsg) {
         stopPolling();
         const {shopId} = getValues();
         onConsumerFound(shopId, completedMsg.result.consumerId);
      }
   }, [polling, messages, stopPolling, getValues, onConsumerFound]);

   const onSubmit = async (data: RequestPotentialsFormData) => {
      setError(undefined);
      const result = await requestPotentials({
         variables: {
            shopId: data.shopId,
            identifier: data.identifier,
            identifierType: data.identifierType,
         }
      });
      const resultTokens = result.data?.ConsumerRequestPotentials || [];
      if (resultTokens.length === 0) {
         setError('No tokens returned');
         return;
      }
      setTokens(resultTokens);
   };

   return <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import consumer data</DialogTitle>
      <DialogContent>
         {polling && !error ? (
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4}}>
               <CircularProgress/>
               <div>Waiting for response...</div>
            </Box>
         ) : (
            <FormProvider {...form}>
               <form onSubmit={handleSubmit(onSubmit)} id="requestPotentialsForm">
                  <FormInputSelect name="shopId" label="Shop" options={shopOptions} required/>
                  <FormInputSelect name="identifierType" label="Identifier type" options={identifierTypeOptions} required/>
                  <FormInputText name="identifier" label="Identifier" autoFocus/>
               </form>
            </FormProvider>
         )}
         {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
         <Button onClick={onClose}>Cancel</Button>
         {!polling && <Button type="submit" form="requestPotentialsForm" variant="contained">Request</Button>}
      </DialogActions>
   </Dialog>;
};
